import axios from 'axios'
import { ElMessage } from 'element-plus'
import adaptiveAPI from '@/utils/adaptiveAPI.js'
import {
  DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL,
  ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL,
  normalizeEcologicalZoneZoomLevel
} from '@/utils/ecologicalZoneRendering'

// 生态管控区API接口常量
export const ECOLOGICAL_API_ENDPOINTS = {
  stats: '/ecological-zones/stats',
  list: '/ecological-zones/list', 
  detail: '/ecological-zones/:id',
  spatial: {
    point: '/ecological-zones/spatial/point-query',
    radius: '/ecological-zones/spatial/radius-query',
    bbox: '/ecological-zones/spatial/bbox-query'
  }
};

// 初始化自适应API配置
let service = null;

// 异步初始化API服务
const initializeService = async () => {
  if (service) return service;
  
  try {
    await adaptiveAPI.initialize();
    service = adaptiveAPI.getAxiosInstance();
    // Ecological Zones API 服务已使用自适应配置初始化
    return service;
  } catch (error) {
    // console.error('❌ Ecological Zones API 初始化失败:', error);
    // 回退到默认配置
    service = axios.create({
      baseURL: process.env.VUE_APP_API_BASE_URL || '/api',
      timeout: 180000
    });
    
    // 设置回退配置的拦截器
    setupFallbackInterceptors(service);
    return service;
  }
};

// 设置回退配置的拦截器
const setupFallbackInterceptors = (serviceInstance) => {
  serviceInstance.interceptors.request.use(
    config => {
      return config
    },
    error => {
      if (process.env.NODE_ENV === 'development') {
        // console.error('生态管控区API请求错误:', error)
      }
      return Promise.reject(error)
    }
  )

  serviceInstance.interceptors.response.use(
    response => {
      const res = response.data
      
      if (res.code && res.code !== 200) {
        ElMessage({
          message: res.message || '生态管控区数据获取失败',
          type: 'error',
          duration: 5 * 1000
        })
        return Promise.reject(new Error(res.message || '生态管控区API错误'))
      } else {
        return res
      }
    },
    error => {
      // console.error('生态管控区API响应错误:', error)
      ElMessage({
        message: error.message || '生态管控区服务异常',
        type: 'error',
        duration: 5 * 1000
      })
      return Promise.reject(error)
    }
  )
};

/**
 * 生态管控区API服务类
 */
export class EcologicalZonesAPI {
  
  /**
   * 获取生态管控区统计信息
   * @returns {Promise} 统计数据
   */
  static async getStats() {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.get(ECOLOGICAL_API_ENDPOINTS.stats)
    return response.data
  }

  /**
   * 获取生态管控区列表
   * @param {Object} params - 查询参数
   * @param {string} params.keyword - 关键词搜索
   * @param {string} params.type - 管控区类型
   * @param {string} params.level - 管控级别
   * @param {number} params.page - 页码
   * @param {number} params.pageSize - 每页数量
   * @returns {Promise} 管控区列表数据
   */
  static async getZonesList(params = {}) {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.get(ECOLOGICAL_API_ENDPOINTS.list, { params })
    return response.data
  }

  /**
   * 获取行政区划列表
   * @returns {Promise} 行政区划数据
   */
  static async getAreas() {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.get('/ecological-zones/areas')
    return response.data
  }

  /**
   * 获取生态管控区详情
   * @param {string|number} id - 管控区ID
   * @returns {Promise} 管控区详情数据
   */
  static async getZoneDetail(id) {
    const serviceInstance = await initializeService()
    const url = ECOLOGICAL_API_ENDPOINTS.detail.replace(':id', id)
    const response = await serviceInstance.get(url)
    return response.data
  }

  /**
   * 空间查询 - 点查询
   * @param {number} longitude - 经度
   * @param {number} latitude - 纬度
   * @param {Object} options - 可选附加参数（如 zoom_level），合并进请求体
   * @returns {Promise} 查询结果
   */
  static async pointQuery(longitude, latitude, options = {}) {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.post(ECOLOGICAL_API_ENDPOINTS.spatial.point, {
      longitude,
      latitude,
      ...options
    })
    return response.data
  }

  /**
   * 空间查询 - 距离查询
   * @param {number} longitude - 经度
   * @param {number} latitude - 纬度
   * @param {number} radius_km - 查询半径(公里)
   * @returns {Promise} 查询结果
   */
  static async radiusQuery(longitude, latitude, radius_km) {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.post(ECOLOGICAL_API_ENDPOINTS.spatial.radius, {
      longitude,
      latitude,
      radius_km
    })
    return response.data
  }

  /**
   * 空间查询 - 边界框查询
   * @param {Object} bbox - 边界框坐标
   * @param {number} bbox.minLng - 最小经度
   * @param {number} bbox.minLat - 最小纬度
   * @param {number} bbox.maxLng - 最大经度
   * @param {number} bbox.maxLat - 最大纬度
   * @param {number} bbox.zoom_level - 缩放级别（可选）
   * @param {number} bbox.limit - 查询数量限制（可选）
   * @returns {Promise} 查询结果
   */
  static async bboxQuery(bbox) {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.post(ECOLOGICAL_API_ENDPOINTS.spatial.bbox, {
      min_longitude: bbox.minLng,
      min_latitude: bbox.minLat,
      max_longitude: bbox.maxLng,
      max_latitude: bbox.maxLat,
      province: bbox.province,
      city: bbox.city,
      county: bbox.county,
      zoom_level: normalizeEcologicalZoneZoomLevel(
        bbox.zoom_level,
        DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL
      ),
      limit: bbox.limit || 1000
    })
    return response.data
  }

  /**
   * 获取管控区关联的应急资源
   * @param {string|number} zoneId - 管控区ID
   * @param {number} radius - 搜索半径(米)
   * @returns {Promise} 关联资源数据
   */
  static async getRelatedEmergencyResources(zoneId, radius = 5000) {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.get(`/ecological-zones/${zoneId}/emergency-resources`, {
      params: { radius }
    })
    return response.data
  }

  /**
   * 获取生态管控区几何数据（仅在需要时调用）
   * @param {Array} ids - 管控区ID数组，如果为空则获取所有
   * @returns {Promise} 几何数据
   */
  static async getZoneGeometry(ids = [], options = {}) {
    const serviceInstance = await initializeService()
    const zoomLevel = normalizeEcologicalZoneZoomLevel(
      options.preferExact ? ECOLOGICAL_ZONE_EXACT_GEOMETRY_ZOOM_LEVEL : options.zoomLevel,
      DEFAULT_ECOLOGICAL_ZONE_ZOOM_LEVEL
    )
    const url = ids.length > 0
      ? `${ECOLOGICAL_API_ENDPOINTS.list}?include_geometry=true&ids=${ids.join(',')}&zoom_level=${zoomLevel}`
      : `${ECOLOGICAL_API_ENDPOINTS.list}?include_geometry=true&limit=1000&zoom_level=${zoomLevel}`
    const response = await serviceInstance.get(url)
    return response.data
  }

  /**
   * 获取筛选条件选项
   * @returns {Promise} 筛选选项数据
   */
  static async getFilterOptions() {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.get('/ecological-zones/filter-options')
    // 返回标准化的响应结构，与其他API保持一致
    return response
  }

  /**
   * 导出生态管控区数据
   * @param {Object} params - 导出参数
   * @param {string} params.format - 导出格式 (excel, geojson, shapefile)
   * @param {Array} params.zoneIds - 要导出的管控区ID列表
   * @returns {Promise} 导出文件信息
   */
  static async exportZonesData(params = {}) {
    const serviceInstance = await initializeService()
    const response = await serviceInstance.post('/ecological-zones/export', params, {
      responseType: 'blob'
    })
    return response.data
  }
}

// 默认导出API类
export default EcologicalZonesAPI;
